import React, { useContext, useEffect, useState } from "react";
import {
  getModifiedScrollHeight,
  toIsoDate,
} from "../../../../../../../utils/UtilFunctions";
import {
  AtsRfqMstEdge,
  AtsRfqMstOrderField,
  GetRfqsResult,
  GetRfqsVars,
  RfqQueryType,
} from "../../../quotation/queries/types";
import {
  FormAutocomplete,
  formClasses,
} from "../../../../../../../styles/AutocompleteStyles";
import { GetRfqs } from "../../../quotation/queries/queries";
import { useLazyQuery } from "@apollo/client";
import {
  EMPTY_STRING,
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
} from "../../../../../../../utils/constants";
import { TextField } from "@mui/material";

import useUrlParams from "../../../../../../../customHooks/useUrlParams";
import { AppContext } from "../../../../../../../context/context";
import { OrderDirection } from "../../../../../../../utils/commonQueryTypes";
import { formDates } from "../../../material/ApprovedList";
import { Props } from "./RFQSelect";

const RFQAutocomplete = ({ value, onChange, onKeyDown, autofocus }: Props) => {
  const [rfqs, setRfqs] = useState<AtsRfqMstEdge[]>([]);

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);

  const [dates, setDates] = useState<formDates>({
    startDate: "",
    endDate: "",
  });

  const { customer_id } = useUrlParams();
  const { state } = useContext(AppContext);



  const [GetRFQ, { loading, data: rfqData, fetchMore }] = useLazyQuery<
    GetRfqsResult,
    GetRfqsVars
  >(GetRfqs);

  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prevResult;

            const newEdges = fetchMoreResult.GetRfqs.edges;
            const pageInfo = fetchMoreResult.GetRfqs.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck = prevResult.GetRfqs.edges.filter(
              ({ node: { id } }) =>
                newEdges.findIndex(
                  ({ node: { id: newId } }) => newId === id
                ) !== -1
            );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetRfqs: {
                edges: [...rfqs, ...newEdges],
                pageInfo,
                totalCount: rfqData ? rfqData.GetRfqs.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };

  useEffect(() => {
    if (customer_id && state.ActiveFinYr) {
      GetRFQ({
        variables: {
          
          after: null,
          customerId: customer_id,
          finYrId: state.ActiveFinYr.id,
          first: ROWS_PER_PAGE,
          name: EMPTY_STRING,
          end_date: toIsoDate(dates.endDate),
          start_date: toIsoDate(dates.startDate),
          queryType: RfqQueryType.ALL,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsRfqMstOrderField.RFQ_DATE,
          },
        },
      });
    }
  }, [customer_id, state.ActiveFinYr, GetRFQ, dates.endDate, dates.startDate]);

  useEffect(() => {
    if (rfqData && !loading) {
      const newData = rfqData.GetRfqs.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = rfqs.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setRfqs(updatedNewData);
      } else {
        setRfqs(newData);
      }
      setEndCursor(rfqData.GetRfqs.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [rfqData, loading]);

  useEffect(() => {
    if (state.ActiveFinYr) {
      setDates({
        endDate: toIsoDate(state.ActiveFinYr.finEndDate),
        startDate: toIsoDate(state.ActiveFinYr.finStDate),
      });
    }
  }, [state.ActiveFinYr]);

  return (
    <FormAutocomplete
      className={formClasses.inputRoot}
      options={rfqs}
      onKeyDown={onKeyDown}
      slotProps={{
        listbox: {
          onScroll: handleScroll,
        },
      }}
      getOptionKey={(option) => (option as AtsRfqMstEdge).node.id}
      getOptionLabel={(option) => (option as AtsRfqMstEdge).node.rfqNo}
      value={value}
      onChange={(e, newValue) => {
        onChange(newValue as AtsRfqMstEdge | null);
      }}
      openOnFocus
      forcePopupIcon
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          autoFocus={autofocus}
          className={formClasses.formControlRoot}
        />
      )}
    />
  );
};

export default RFQAutocomplete;
